/** Eigener Zähler als Kachel (BE-06 Teil 3): hoch- und runterzählen, optional mit Ziel und Balken. */
import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Minus, Plus } from 'lucide-react';

import { db } from '@/db/schema';
import { zaehlerAendern, zaehlerErhoehen } from '@/db/repositories/oberflaeche';
import { zaehlerAnteil, zaehlerAus } from '@/domain/oberflaeche/zaehler';
import { StatKarte } from '@/ui/composites/Statistik';
import { Input } from '@/ui/primitives/Input';

function useZaehler(id: string) {
  return useLiveQuery(async () => zaehlerAus(await db.oberflaeche.get(id)), [id]);
}

export function ZaehlerKachel({ id }: { id: string }) {
  const z = useZaehler(id);
  const [ziel, setZiel] = useState<string | null>(null);
  if (!z) return null;
  const anteil = zaehlerAnteil(z);
  const knopf = 'grid size-9 place-items-center rounded-md border border-line text-ink-muted hover:border-line-strong hover:text-ink';
  return (
    <StatKarte titel={z.name}>
      <div className="flex items-center gap-3">
        <button type="button" className={knopf} aria-label={`${z.name} um 1 verringern`} onClick={() => void zaehlerErhoehen(id, -1)}><Minus className="size-4" /></button>
        <p className="flex-1 text-center text-[32px] font-extrabold leading-none tabular-nums" aria-live="polite">
          {z.wert}{z.ziel ? <span className="text-[16px] font-medium text-ink-faint"> / {z.ziel}</span> : null}
        </p>
        <button type="button" className={knopf} aria-label={`${z.name} um 1 erhöhen`} onClick={() => void zaehlerErhoehen(id, 1)}><Plus className="size-4" /></button>
      </div>
      {z.darstellung === 'balken' && anteil !== null && (
        <div className="h-2 overflow-hidden rounded-full bg-surface-raised" role="progressbar" aria-label={`Fortschritt ${z.name}`} aria-valuemin={0} aria-valuemax={z.ziel} aria-valuenow={Math.min(z.wert, z.ziel ?? z.wert)}>
          <div className="h-full rounded-full bg-accent" style={{ width: `${Math.round(anteil * 100)}%` }} />
        </div>
      )}
      <div className="flex items-center gap-2 text-[12px] text-ink-muted">
        <label className="flex w-28 items-center gap-1.5">Ziel
          <Input inputMode="numeric" aria-label={`Ziel ${z.name}`} className="h-7 px-2 text-[12px]" placeholder="kein" value={ziel ?? (z.ziel ? String(z.ziel) : '')} onChange={(e) => setZiel(e.target.value)}
            onBlur={() => { if (ziel !== null) { const n = Number(ziel); void zaehlerAendern(id, n > 0 ? { ziel: n, darstellung: z.darstellung } : { ziel: undefined, darstellung: 'zahl' }); setZiel(null); } }} />
        </label>
        {z.ziel ? (
          <button type="button" className="ml-auto text-accent-text hover:underline" onClick={() => void zaehlerAendern(id, { darstellung: z.darstellung === 'balken' ? 'zahl' : 'balken' })}>
            {z.darstellung === 'balken' ? 'Als Zahl zeigen' : 'Als Balken zeigen'}
          </button>
        ) : null}
      </div>
    </StatKarte>
  );
}
